import type { ApiResponse, ActionRequiredGroup, Batch, BatchSummary, Order, User, WhitelistedUser, ScheduleResponse, RotationConfig, AiAccuracyResponse, RefundForecast, RefundForecastDate, StaleOrdersDate, StaleOrdersReport, RefundBlockedDate, RefundBlockedReport, TriageBatch, TriageBatchSummary, TriageItem, TriageQueueId } from '../types';
import type { AiModelKey } from '../utils/aiModels';

const BASE_URL = '';

async function fetchApi<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const response = await fetch(`${BASE_URL}${endpoint}`, {
    ...options,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

  const data: ApiResponse<T> = await response.json();

  if (!data.success) {
    throw new Error(data.error || 'Request failed');
  }

  return data.data as T;
}

// Auth API
export async function getCurrentUser(): Promise<User> {
  return fetchApi<User>('/auth/me');
}

export async function logout(): Promise<void> {
  await fetch(`${BASE_URL}/auth/logout`, {
    method: 'POST',
    credentials: 'include',
  });
}

export function getSlackLoginUrl(): string {
  return `${BASE_URL}/auth/slack`;
}

// Batches API
export async function listBatches(): Promise<BatchSummary[]> {
  return fetchApi<BatchSummary[]>('/api/batches');
}

export async function getBatch(date: string): Promise<{ batch: Batch; orders: Order[] }> {
  return fetchApi<{ batch: Batch; orders: Order[] }>(`/api/batches/${date}`);
}

export async function reprocessBatch(date: string, aiModel?: AiModelKey): Promise<{ message: string }> {
  return fetchApi<{ message: string }>(`/api/batches/${date}/reprocess`, {
    method: 'POST',
    body: JSON.stringify({ aiModel }),
  });
}

// Action Required API
export async function getActionRequiredOrders(): Promise<ActionRequiredGroup[]> {
  return fetchApi<ActionRequiredGroup[]>('/api/orders/action-required');
}

// Orders API
export async function updateOrder(
  date: string,
  orderId: string,
  status: 'pending' | 'resolved',
  notes?: string
): Promise<Order> {
  return fetchApi<Order>(`/api/orders/${date}/${orderId}`, {
    method: 'PATCH',
    body: JSON.stringify({ status, notes }),
  });
}

// Check Status API
export async function checkOrderStatus(
  date: string,
  orderId: string
): Promise<Order> {
  return fetchApi<Order>(`/api/orders/${date}/${orderId}/check-status`, {
    method: 'POST',
  });
}

// AI Analysis API
export async function triggerAiAnalysis(
  date: string,
  orderId: string,
  aiModel?: AiModelKey
): Promise<{ message: string }> {
  return fetchApi<{ message: string }>(`/api/orders/${date}/${orderId}/ai-analyze`, {
    method: 'POST',
    body: JSON.stringify({ aiModel }),
  });
}

// CS Notification API
export async function notifyCs(
  date: string,
  orderId: string
): Promise<{ message: string }> {
  return fetchApi<{ message: string }>(`/api/orders/${date}/${orderId}/notify-cs`, {
    method: 'POST',
  });
}

// Dev Team Notification API
export async function notifyDevTeam(
  date: string,
  orderId: string
): Promise<{ message: string; affectedOrderCount?: number }> {
  return fetchApi<{ message: string; affectedOrderCount?: number }>(`/api/orders/${date}/${orderId}/notify-dev`, {
    method: 'POST',
  });
}

// Export Job API
export async function exportJob(
  date: string,
  orderId: string
): Promise<{ message: string; orderId?: number; error?: string; statusCode?: number }> {
  return fetchApi<{ message: string; orderId?: number; error?: string; statusCode?: number }>(`/api/orders/${date}/${orderId}/export-job`, {
    method: 'POST',
  });
}

// Resubmit Order API
export async function resubmitOrder(
  date: string,
  orderId: string
): Promise<{ message: string; orderId?: string; error?: string; statusCode?: number }> {
  return fetchApi<{ message: string; orderId?: string; error?: string; statusCode?: number }>(`/api/orders/${date}/${orderId}/resubmit`, {
    method: 'POST',
  });
}

// AI Accuracy API
export async function getAiAccuracy(limit: number = 10): Promise<AiAccuracyResponse> {
  return fetchApi<AiAccuracyResponse>(`/api/ai-accuracy?limit=${limit}`);
}

// Refund Forecast API
export async function getForecastDates(): Promise<RefundForecastDate[]> {
  return fetchApi<RefundForecastDate[]>('/api/forecast/dates');
}

export async function getForecast(date: string): Promise<RefundForecast> {
  return fetchApi<RefundForecast>(`/api/forecast/${date}/skus`);
}

// Stale OMS Orders API (read-only)
export async function getStaleOrdersDates(): Promise<StaleOrdersDate[]> {
  return fetchApi<StaleOrdersDate[]>('/api/stale-orders/dates');
}

export async function getStaleOrders(date: string): Promise<StaleOrdersReport> {
  return fetchApi<StaleOrdersReport>(`/api/stale-orders/${date}`);
}

// Blocked Refunds API (read-only)
export async function getBlockedRefundsDates(): Promise<RefundBlockedDate[]> {
  return fetchApi<RefundBlockedDate[]>('/api/blocked-refunds/dates');
}

export async function getBlockedRefunds(date: string): Promise<RefundBlockedReport> {
  return fetchApi<RefundBlockedReport>(`/api/blocked-refunds/${date}`);
}

// Triage Queues API (see TRIAGE_QUEUES.MD)
export async function listTriageBatches(queue: TriageQueueId): Promise<TriageBatchSummary[]> {
  return fetchApi<TriageBatchSummary[]>(`/api/triage/${queue}/batches`);
}

export async function getTriageBatch(
  queue: TriageQueueId,
  date: string
): Promise<{ batch: TriageBatch; items: TriageItem[] }> {
  return fetchApi<{ batch: TriageBatch; items: TriageItem[] }>(`/api/triage/${queue}/batches/${date}`);
}

export async function checkTriageItemStatus(
  queue: TriageQueueId,
  date: string,
  itemId: string
): Promise<TriageItem> {
  return fetchApi<TriageItem>(
    `/api/triage/${queue}/items/${date}/${encodeURIComponent(itemId)}/check-status`,
    { method: 'POST' }
  );
}

export async function exportTriageItem(
  queue: TriageQueueId,
  date: string,
  itemId: string
): Promise<{ message: string; shipMemoId?: string; orderId?: string; error?: string; statusCode?: number }> {
  return fetchApi<{ message: string; shipMemoId?: string; orderId?: string; error?: string; statusCode?: number }>(
    `/api/triage/${queue}/items/${date}/${encodeURIComponent(itemId)}/export`,
    { method: 'POST' }
  );
}

export async function updateTriageItem(
  queue: TriageQueueId,
  date: string,
  itemId: string,
  status: 'pending' | 'resolved',
  notes?: string
): Promise<TriageItem> {
  return fetchApi<TriageItem>(`/api/triage/${queue}/items/${date}/${encodeURIComponent(itemId)}`, {
    method: 'PATCH',
    body: JSON.stringify({ status, notes }),
  });
}

// Admin API
export async function listUsers(): Promise<WhitelistedUser[]> {
  return fetchApi<WhitelistedUser[]>('/api/admin/users');
}

export async function addUser(email: string, isAdmin?: boolean): Promise<WhitelistedUser> {
  return fetchApi<WhitelistedUser>('/api/admin/users', {
    method: 'POST',
    body: JSON.stringify({ email, isAdmin }),
  });
}

export async function updateUserAdmin(email: string, isAdmin: boolean): Promise<WhitelistedUser> {
  return fetchApi<WhitelistedUser>(`/api/admin/users/${encodeURIComponent(email)}`, {
    method: 'PATCH',
    body: JSON.stringify({ isAdmin }),
  });
}

export async function removeUser(email: string): Promise<void> {
  return fetchApi<void>(`/api/admin/users/${encodeURIComponent(email)}`, {
    method: 'DELETE',
  });
}

// Schedule API
export async function getSchedule(start: string, weeks: number): Promise<ScheduleResponse> {
  return fetchApi<ScheduleResponse>(`/api/admin/schedule?start=${start}&weeks=${weeks}`);
}

export async function upsertShift(
  weekStart: string,
  assigneeEmail: string,
  assigneeName: string
): Promise<unknown> {
  return fetchApi(`/api/admin/schedule/shifts/${weekStart}`, {
    method: 'POST',
    body: JSON.stringify({ assigneeEmail, assigneeName }),
  });
}

export async function deleteShift(weekStart: string): Promise<void> {
  return fetchApi<void>(`/api/admin/schedule/shifts/${weekStart}`, {
    method: 'DELETE',
  });
}

export async function fetchRotationConfig(): Promise<RotationConfig | null> {
  return fetchApi<RotationConfig | null>('/api/admin/schedule/rotation');
}

export async function updateRotationConfig(
  config: { enabled: boolean; members: Array<{ email: string; name: string }>; startDate: string; periodDays: number }
): Promise<RotationConfig> {
  return fetchApi<RotationConfig>('/api/admin/schedule/rotation', {
    method: 'POST',
    body: JSON.stringify(config),
  });
}
